import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Input, InputGroup, InputGroupAddon, InputGroupText} from "reactstrap";
import {debounceTime, distinctUntilChanged, Subject} from "rxjs";
import useDidMountEffect from "../utils/useDidMountEffect";
import useAfterUpdateEffect from "../utils/useAfterUpdateEffect";
import {PolishVehicleRegistrationCertificateDecoder} from "polish-vehicle-registration-certificate-decoder";
import getWorkshop from "../utils/getWorkshop";
import reverseStringChars from "../utils/reverseStringChars";
import apiUser from "../apiUser";
import {CLIENT_COMPANY_TYPE, CLIENT_PERSON_TYPE} from "../utils/client";
import Icon from "../componentsReusable/Icon";
import AztecCameraModal from "./AztecCameraModal";
import {isDesktop} from "react-device-detect";
import Button from "./Button";
import {useTranslation} from "react-i18next";

const InputAztec = props => {
  const { t } = useTranslation('aztec_input');
  const [capsLock, setCapsLock] = useState(false);
  const [value, setValue] = useState('');
  const [value$, setValue$] = useState(new Subject());
  const [error, setError] = useState(false);
  const [decoded, setDecoded] = useState(false);
  const [aztecCameraOpen, setAztecCameraOpen] = useState(false)

  useDidMountEffect(() => {
    value$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
    ).subscribe(decode)
  })

  useAfterUpdateEffect(() => {
    setError(false);
    setDecoded(false);
    value$.next(capsLock ? reverseStringChars(value) : value)
  }, [value])

  const decode = (value) => {
    setError(false)
    setDecoded(false)

    if(!value){
      return;
    }

    decodeAztec(value)
  }

  const decodeAztec = (value) => {
    try{
      const decoded = new PolishVehicleRegistrationCertificateDecoder(capsLock ? reverseStringChars(value) : value)

      if(decoded?.data){
        let vehicle = {
          firstRegistration: decoded.data?.dataPierwszejRejestracjiPojazdu?.value,
          vin: decoded.data?.numerIdentyfikacyjnyPojazdu?.value || '',
          productionDate: decoded.data?.rokProdukcji?.value || '',
          brand: decoded.data?.markaPojazdu?.value || '',
          model: decoded.data?.modelPojazdu?.value || '',
          registrationNumber: decoded.data?.numerRejestracyjnyPojazdu?.value || '',
          capacity: decoded.data?.pojemnoscSilnikaCm3?.value || '',
          enginePower: decoded.data?.maksymalnaMocNettoSilnikaKW?.value || '',
          engine: decoded.data?.rodzajPaliwa?.value || '',
          enginePowerUnit: getWorkshop().settingDefaultEnginePowerUnit,
        }

        let client;
        if(decoded.data?.numerPESELLubREGONPosiadaczaDowoduRejestracyjnego.value.length === 11){
          client = {
            type: CLIENT_PERSON_TYPE,
            firstname: decoded.data?.imiePosiadaczaDowoduRejestracyjnego?.value,
            lastname: decoded.data?.nazwiskoPosiadaczaDowoduRejestracyjnego?.value,
            postalCode: decoded.data?.kodPocztowyPosiadaczaDowoduRejestracyjnego?.value,
            city: decoded.data?.miejscowoscPosiadaczaDowoduRejestracyjnego?.value,
            street: [decoded.data?.ulicaPosiadaczaDowoduRejestracyjnego?.value || decoded.data?.miejscowoscPosiadaczaDowoduRejestracyjnego?.value, decoded.data?.nrDomuPosiadaczaDowoduRejestracyjnego?.value].filter(v => !!v).join(' '),
          }

          setDecoded(true)
          props.onDecoded({
            vehicle,
            client,
          })
        }else{
          apiUser.gus.find({
            regon: decoded.data?.numerPESELLubREGONPosiadaczaDowoduRejestracyjnego.value.substring(0, 9),
          }).then(gusData => {
            client = {
              type: CLIENT_COMPANY_TYPE,
              name: gusData.name,
              nip: gusData.nip,
              city: gusData.city,
              postalCode: gusData.zipCode,
              street: [gusData.street ? gusData.street : gusData.city, [gusData.propertyNumber, gusData.apartmentNumber].filter(v => !!v).join(' / ')].join(' ')
            }

            setDecoded(true)
            props.onDecoded({
              vehicle,
              client,
            })
          })
        }
      }
    }catch (e){
      setError(true)
    }
  }

  return <React.Fragment>
    {
      isDesktop
        ? <InputGroup>
          <InputGroupAddon addonType={'prepend'}>
            <InputGroupText>
              <Icon className="fas fa-qrcode" tooltip={t('icon_tooltip')} />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            invalid={!!error}
            bsSize={props.size}
            valid={decoded}
            autoFocus
            placeholder="AZTEC"
            onKeyDown={e => {
              if(e.getModifierState('CapsLock')){
                setCapsLock(true)
              }else{
                setCapsLock(false)
              }
            }}
            onChange={e => setValue(e.target.value)}
            value={value}
          />
          <InputGroupAddon addonType={'append'} className="cursor-pointer" onClick={() => setAztecCameraOpen(true)}>
            <InputGroupText>
              <Icon className="fas fa-camera" tooltip={t('camera_text')} />
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        : <Button size={props.size} color="secondary" onClick={() => setAztecCameraOpen(true)}>
        <i className="fas fa-camera" />
        <span className="d-none d-md-block">{t('camera_text')}</span>
        </Button>
    }
    {aztecCameraOpen && <AztecCameraModal
      onClose={() => setAztecCameraOpen(false)}
      onDecoded={aztec => {
        setAztecCameraOpen(false)
        setValue(aztec)
      }}
    />}
  </React.Fragment>
};

InputAztec.defaultProps = {
  size: 'md',
};

InputAztec.propTypes = {
  size: PropTypes.string,
  onDecoded: PropTypes.func.isRequired,
};

export default InputAztec;
